import React, { useEffect, useState } from 'react';
import { MemoryRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { announceSub } from './utils/navSignal';
import Brands from './pages/dashboard/Brands';
import Branches from './pages/dashboard/Branches';
import MenuEditor from './pages/menu/MenuEditor';
import BranchMenuEditor from './pages/menu/BranchMenuEditor';
import PriceEditor from './pages/menu/PriceEditor';
import PriceListEditor from './pages/menu/PriceListEditor';
import QRPage from './pages/dashboard/QRPage';
import Users from './pages/dashboard/Users';
import ImportMenu from './pages/import/ImportMenu';
import Layout from './components/layout/Layout';
import SlideBuilder from './pages/menuboard/SlideBuilder';
import SlideManager from './pages/menuboard/SlideManager';
import InfoScreens from './pages/menuboard/InfoScreens';
import BrandSelect from './pages/dashboard/BrandSelect';
import './menu-app.css';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh'}}>Loading…</div>;
  return user ? children : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',color:'red'}}>Not authenticated</div>;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/" />;
  if (user.role !== 'admin') return <Navigate to="/" />;
  return children;
};

const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
      <Route index element={<Brands />} />
      <Route path="brands" element={<Brands />} />
      <Route path="brands/:brandId/branches" element={<Branches />} />
      <Route path="brands/:brandId/menu" element={<MenuEditor />} />
      <Route path="brands/:brandId/branches/:branchId/menu" element={<AdminRoute><BranchMenuEditor /></AdminRoute>} />
      <Route path="brands/:brandId/price-list" element={<PriceListEditor />} />
      <Route path="brands/:brandId/qr" element={<QRPage />} />
      <Route path="brands/:brandId/slides" element={<SlideManager />} />
      <Route path="brands/:brandId/info-screens" element={<InfoScreens />} />
      {/* Door Signs was the working name; keep the old path pointing at it so
          any bookmark or stale sessionStorage path still lands somewhere. */}
      <Route path="brands/:brandId/door-signs" element={<InfoScreens />} />
      <Route path="brands/:brandId/slide-builder/new" element={<SlideBuilder />} />
      <Route path="brands/:brandId/slide-builder/:slideId" element={<SlideBuilder />} />
      <Route path="brands/:brandId/slides/:screenId" element={<SlideBuilder />} />
      <Route path="branches/:branchId/qr" element={<QRPage />} />
      <Route path="branches/:branchId/menuboard/:screenId" element={<Navigate to="/" replace />} />
      <Route path="branches/:branchId/menuboard" element={<Navigate to="/" replace />} />
      <Route path="price-edit" element={<PriceEditor />} />
      <Route path="brand-select" element={<BrandSelect />} />
      <Route path="users" element={<AdminRoute><Users /></AdminRoute>} />
      {/* Staff / User Manager removed 2026-10-27 (owner). The path stays
          mapped so a stale bookmark or a saved sessionStorage location
          lands home instead of on a blank screen. */}
      <Route path="staff" element={<Navigate to="/" replace />} />
      <Route path="users/:userId/branches" element={<AdminRoute><Branches /></AdminRoute>} />
      <Route path="import" element={<AdminRoute><ImportMenu /></AdminRoute>} />
    </Route>
    <Route path="*" element={<Navigate to="/" />} />
  </Routes>
);

// Slide Builder is deliberately absent from this map: the path alone cannot
// tell a template from a menu slide, so the builder announces for itself once
// it knows what it loaded.
function subForPath(pathname, search) {
  if (/\/(info-screens|door-signs)\b/.test(pathname)) return 'info-screens';
  if (/\/slides\b/.test(pathname)) {
    // The Templates tab of the Slides page belongs to Info Screens — that is
    // what those slides are for.
    return /kind=doorsign/.test(search || '') ? 'info-screens' : 'slides';
  }
  if (/\/(menu|price-list|qr)\b/.test(pathname)) return 'menu';
  if (/^\/import\b/.test(pathname))  return 'import';
  if (/\/branches\b/.test(pathname)) return 'branches';
  return null;   // unknown → leave the highlight alone
}

function LocationSaver() {
  const location = useLocation();
  useEffect(() => {
    try { sessionStorage.setItem('menu_path', location.pathname + location.search); } catch {}
    if (/\/slide-builder\b/.test(location.pathname)) return;  // the builder speaks for itself
    const s = subForPath(location.pathname, location.search);
    if (s) announceSub(s);
  }, [location]);
  return null;
}

function NavigationListener() {
  const navigate = useNavigate();
  useEffect(() => {
    if (window.__menuapp_initial_path) {
      navigate(window.__menuapp_initial_path, window.__menuapp_initial_opts || {});
      window.__menuapp_initial_path = null;
      window.__menuapp_initial_opts = null;
    }
    const h = (e) => navigate(e.detail.path, e.detail.opts || {});
    window.addEventListener('menuapp:navigate', h);
    return () => window.removeEventListener('menuapp:navigate', h);
  }, [navigate]);
  return null;
}

export default function MenuApp() {
  const [initialPath] = useState(() => {
    try { return sessionStorage.getItem('menu_path') || '/'; } catch { return '/'; }
  });
  return (
    <AuthProvider>
      <MemoryRouter initialEntries={[initialPath]}>
        <LocationSaver />
        <NavigationListener />
        <AppRoutes />
        <Toaster position="top-right" />
      </MemoryRouter>
    </AuthProvider>
  );
}
